import { createContext, useContext, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const EVENT_TYPES = {
  Individual: ["Wedding", "Engagement", "Reception", "Anniversary", "Birthday", "Kitty Party", "Private Party", "Social Gathering", "Religious / Social Function", "Other"],
  Corporate: ["Corporate Event", "Conference", "Meeting", "Product Launch", "Employee Meal / Party", "Office Gathering", "Institutional Function", "Other"],
};

const EnquiryContext = createContext(null);
export const useEnquiry = () => useContext(EnquiryContext);

const FIELD = "w-full rounded-none border-0 border-b border-subtle bg-transparent px-0 py-2 text-[15px] text-espresso focus-visible:ring-0 focus-visible:border-gold placeholder:text-inkmuted/60";

function Field({ label, name, form, setForm, type = "text", required, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-wider text-inkmuted">{label}{required && <span className="text-gold"> *</span>}</Label>
      <Input data-testid={testid} type={type} required={required} placeholder={placeholder}
        value={form[name] || ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })} className={FIELD} />
    </div>
  );
}
function Area({ label, name, form, setForm, testid }) {
  return (
    <div className="space-y-1.5 sm:col-span-2">
      <Label className="text-[11px] uppercase tracking-wider text-inkmuted">{label}</Label>
      <Textarea data-testid={testid} rows={3} value={form[name] || ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="w-full rounded-md border border-subtle bg-transparent p-3 text-[15px] text-espresso focus-visible:ring-gold" />
    </div>
  );
}

const TITLES = {
  event: { t: "Event Catering Enquiry", d: "Weddings, celebrations & corporate events — crafted around your occasion.", ep: "/enquiries/event", cta: "Submit Event Enquiry", tid: "event" },
  bulk: { t: "Bulk & Food Service Enquiry", d: "Daily meals, institutional & recurring food services at scale.", ep: "/enquiries/bulk", cta: "Submit Food Service Enquiry", tid: "bulk" },
  contact: { t: "Get in Touch", d: "Tell us about your requirement and our team will reach out.", ep: "/enquiries/contact", cta: "Send Enquiry", tid: "contact" },
};

export function EnquiryProvider({ children }) {
  const [type, setType] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const open = (t) => { setForm({}); setType(t); };
  const close = () => setType(null);
  const cfg = type ? TITLES[type] : null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(cfg.ep, form);
      toast.success(data.message || "Enquiry received. We'll be in touch shortly.");
      close();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <EnquiryContext.Provider value={{ open }}>
      {children}
      <Dialog open={!!type} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-ivory sm:max-w-2xl border-subtle">
          {cfg && (
            <>
              <DialogHeader>
                <div className="text-[11px] font-semibold uppercase tracking-editorial text-gold">Mee2Momos Hospitality</div>
                <DialogTitle className="font-display text-3xl font-normal text-espresso">{cfg.t}</DialogTitle>
                <DialogDescription className="text-inkmuted">{cfg.d}</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="mt-2 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-testid={`enquiry-form-${cfg.tid}`}>
                <Field label="Name" name="name" form={form} setForm={setForm} required testid={`${cfg.tid}-name`} />
                <Field label={type === "contact" ? "Organisation" : "Company / Organisation"} name={type === "contact" ? "organisation" : "company"} form={form} setForm={setForm} testid={`${cfg.tid}-company`} />
                {type === "bulk" && <Field label="Designation" name="designation" form={form} setForm={setForm} testid="bulk-designation" />}
                <Field label="Phone" name="phone" form={form} setForm={setForm} required testid={`${cfg.tid}-phone`} />
                <Field label="Email" name="email" type="email" form={form} setForm={setForm} required testid={`${cfg.tid}-email`} />

                {type === "event" && <>
                  <div className="sm:col-span-2">
                    <Label className="text-[11px] uppercase tracking-wider text-inkmuted">What kind of event?</Label>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      {["Individual", "Corporate"].map((c) => (
                        <button type="button" key={c} data-testid={`event-category-${c.toLowerCase()}`}
                          onClick={() => setForm({ ...form, enquiry_category: c, event_type: "" })}
                          className={`rounded-none border px-4 py-3 text-[12px] font-semibold uppercase tracking-wider transition-colors ${form.enquiry_category === c ? "border-espresso bg-espresso text-ivory" : "border-subtle text-espresso/70 hover:border-gold"}`}>
                          {c === "Individual" ? "Individual / Social" : "Corporate"}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-[11px] uppercase tracking-wider text-inkmuted">Event Type<span className="text-gold"> *</span></Label>
                    <Select value={form.event_type || ""} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                      <SelectTrigger data-testid="event-type" className="w-full rounded-none border-0 border-b border-subtle bg-transparent px-0"><SelectValue placeholder={form.enquiry_category ? "Select event type" : "Choose a category first"} /></SelectTrigger>
                      <SelectContent>{(EVENT_TYPES[form.enquiry_category] || EVENT_TYPES.Individual).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Field label="Event Date" name="event_date" type="date" form={form} setForm={setForm} testid="event-date" />
                  <Field label="Event Time" name="event_time" type="time" form={form} setForm={setForm} testid="event-time" />
                  <Field label="Venue" name="venue" form={form} setForm={setForm} testid="event-venue" />
                  <Field label="City / Location" name="city" form={form} setForm={setForm} testid="event-city" />
                  <Field label="Expected Guest Count" name="guest_count" form={form} setForm={setForm} testid="event-guests" />
                  <Field label="Cuisine Preference" name="cuisine_pref" form={form} setForm={setForm} testid="event-cuisine" />
                  <Field label="Service Style" name="service_style" form={form} setForm={setForm} placeholder="Buffet, Plated, Live counters…" testid="event-style" />
                  <Field label="Budget Range" name="budget" form={form} setForm={setForm} testid="event-budget" />
                  <Field label="Dietary Requirements" name="dietary" form={form} setForm={setForm} placeholder="Jain, no onion/garlic…" testid="event-dietary" />
                  <Area label="Special Requirements" name="special_requirements" form={form} setForm={setForm} testid="event-special" />
                </>}

                {type === "bulk" && <>
                  <Field label="Service Type" name="service_type" form={form} setForm={setForm} placeholder="Daily Meals, Office, Institutional…" testid="bulk-service-type" />
                  <Field label="Expected No. of Meals" name="meals_count" form={form} setForm={setForm} testid="bulk-meals" />
                  <Field label="Frequency" name="frequency" form={form} setForm={setForm} placeholder="Daily / Weekly / Monthly" testid="bulk-frequency" />
                  <Field label="Required Start Date" name="start_date" type="date" form={form} setForm={setForm} testid="bulk-start" />
                  <Field label="Location" name="location" form={form} setForm={setForm} testid="bulk-location" />
                  <Field label="Cuisine Preferences" name="cuisine_pref" form={form} setForm={setForm} testid="bulk-cuisine" />
                  <Field label="Budget Range" name="budget" form={form} setForm={setForm} testid="bulk-budget" />
                  <Field label="Contract Duration" name="contract_duration" form={form} setForm={setForm} testid="bulk-duration" />
                  <Area label="Meal Requirements" name="meal_requirements" form={form} setForm={setForm} testid="bulk-meal-req" />
                  <Area label="Special Requirements" name="special_requirements" form={form} setForm={setForm} testid="bulk-special" />
                </>}

                {type === "contact" && <Field label="Requirement Type" name="requirement_type" form={form} setForm={setForm} placeholder="Event, Bulk, Corporate, Hospitality…" testid="contact-req-type" />}

                <Area label="Additional Message" name="message" form={form} setForm={setForm} testid={`${cfg.tid}-message`} />

                <div className="sm:col-span-2 pt-2">
                  <Button data-testid={`${cfg.tid}-submit-button`} type="submit" disabled={loading}
                    className="w-full rounded-none bg-espresso py-6 text-[13px] font-semibold uppercase tracking-editorial text-ivory hover:bg-gold transition-colors">
                    {loading ? "Sending…" : cfg.cta}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </EnquiryContext.Provider>
  );
}
